// pages/UpgradeEventSelection.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiCalendar,
  FiMapPin,
  FiArrowRight,
  FiRefreshCw,
  FiTrendingUp,
  FiUsers,
  FiImage
} from 'react-icons/fi';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../services/auth';
import supabase from '../services/supabase';
import '../styles/UpgradeEventSelection.scss';
import { parseLocalDate } from '../shared/eventDateTime';

const getTierBadgeClass = (tierName) => {
  switch (tierName?.toLowerCase()) {
    case 'elite': return 'tier-elite';
    case 'pro': return 'tier-pro';
    case 'basic': return 'tier-basic';
    default: return 'tier-free';
  }
};

const EventCard = ({ event, onSelect }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'Date not set';
    return (parseLocalDate(dateString) ?? new Date(NaN)).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const getImageUrl = () => {
    return event.cover_image_cdn_url ||
           event.gif_cover_cdn_url ||
           event.cover_image_direct_url ||
           '/images/events/1.jpg';
  };

  const isPaidEvent = event.pass_type === 'paid' ||
                      (event.passes && event.passes.some(p => parseFloat(p.price) > 0));

  // Get tier display names
  const hostTierName = event.host_tier?.display_name || event.host_tier?.tier_name || 'Free';
  const photoTierName = event.photo_tier?.display_name || event.photo_tier?.tier_name || 'Free';

  return (
    <motion.div
      className="upgrade-event-card"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      onClick={() => onSelect(event)}
    >
      <div className="event-image">
        <img src={getImageUrl()} alt={event.title} />
        <div className="event-overlay"></div>
        {isPaidEvent && (
          <div className="paid-badge">
            <span>Paid Event</span>
          </div>
        )}
      </div>

      <div className="event-content">
        <h3 className="event-title">{event.title}</h3>

        {/* Current Tier Badges */}
        <div className="tier-badges">
          <div className={`tier-badge ${getTierBadgeClass(event.host_tier?.tier_name)}`}>
            <FiUsers size={12} />
            <span>{hostTierName}</span>
          </div>
          <div className={`tier-badge ${getTierBadgeClass(event.photo_tier?.tier_name)}`}>
            <FiImage size={12} />
            <span>{photoTierName}</span>
          </div>
        </div>

        <div className="event-details">
          <div className="detail-item">
            <FiCalendar size={14} />
            <span>{formatDate(event.event_date)}</span>
          </div>

          <div className="detail-item">
            <FiMapPin size={14} />
            <span>{event.location_name || 'Location not set'}</span>
          </div>
        </div>

        <button className="select-btn">
          <span>Upgrade Event</span>
          <FiArrowRight />
        </button>
      </div>
    </motion.div>
  );
};

const UpgradeEventSelection = () => {
  const navigate = useNavigate();
  // A tab asked for on the way here (/upgrade?tab=photo) goes on to the plans.
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [events, setEvents] = useState([]);

  const fetchEvents = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Fetch events with tier information
      const { data, error } = await supabase
        .from('events')
        .select(`
          id,
          title,
          event_date,
          location_name,
          cover_image_cdn_url,
          gif_cover_cdn_url,
          cover_image_direct_url,
          pass_type,
          passes,
          status,
          host_pricing_tier_id,
          photo_pricing_tier_id,
          host_tier:event_pricing_tiers!host_pricing_tier_id(
            id,
            tier_name,
            display_name,
            price
          ),
          photo_tier:event_pricing_tiers!photo_pricing_tier_id(
            id,
            tier_name,
            display_name,
            price
          )
        `)
        .eq('host_id', user.id)
        .eq('status', 'published')
        .order('event_date', { ascending: false });

      if (error) {
        console.error('Error fetching events:', error);
        // Fallback query without tier info
        const { data: fallbackData } = await supabase
          .from('events')
          .select('*')
          .eq('host_id', user.id)
          .eq('status', 'published')
          .order('event_date', { ascending: false });

        setEvents(fallbackData || []);
      } else {
        setEvents(data || []);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user]);

  useEffect(() => {
    fetchEvents();
  }, [user, fetchEvents]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchEvents();
  };

  const handleSelectEvent = (event) => {
    // Navigate to upgrade tiers page with event data
    navigate(`/upgrade/tiers?eventId=${event.id}&eventName=${encodeURIComponent(event.title)}${tab ? `&tab=${encodeURIComponent(tab)}` : ''}`);
  };

  return (
    <DashboardLayout>
      <div className="upgrade-event-selection-page">
        <div className="selection-container">
          {/* Header */}
          <motion.div 
            className="selection-header"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="header-content">
              <div className="header-icon">
                <FiTrendingUp size={32} />
              </div>
              <div className="header-text">
                <h1>Upgrade Your Event</h1>
                <p>Select an event to unlock premium features</p>
              </div>
              <button 
                className="refresh-btn"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <FiRefreshCw size={20} className={refreshing ? 'spinning' : ''} />
              </button>
            </div>
          </motion.div>

          {/* Features Info */}
          <motion.div 
            className="features-banner"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h3>✨ What You Can Upgrade</h3>
            <div className="features-list">
              <div className="feature-item">
                <span className="icon">📊</span>
                <span>Host Features: Unlimited guests, notification blasts, and more</span>
              </div>
              <div className="feature-item">
                <span className="icon">📸</span>
                <span>Photo Upload: Share more memories with increased photo limits</span>
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <div className="selection-content">
            {loading && !refreshing ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading your events...</p>
              </div>
            ) : events.length > 0 ? (
              <motion.div 
                className="events-grid"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                {events.map((event, index) => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <EventCard event={event} onSelect={handleSelectEvent} />
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              <motion.div 
                className="empty-state"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <FiCalendar size={60} />
                <h3>No events to upgrade</h3>
                <p>Create an event first to access upgrade features</p>
                <button 
                  className="btn btn-primary"
                  onClick={() => alert('Download the mobile app to create events!')}
                >
                  Learn More
                </button>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default UpgradeEventSelection;